/**
 * The (country × role) assignment editor — used by the create form and the
 * detail page's role card. AUTHORED; assembled from house parts (TextField
 * geometry for the selects, the bordered chip for rows).
 *
 * ===========================================================================
 * THE COUNTRY OPTIONS ARE THE D5 BOUNDARY, RENDERED
 * ===========================================================================
 * `countries` is the caller's manageable set (`useManageableCountries` — the
 * client half of `authorityCountries()` in d5-boundary.ts). A country the
 * caller cannot administer is not a disabled option, it is NOT an option: a
 * visible-but-disabled "IN" would advertise a capability the server refuses,
 * and D5's rule is that the UI hides what the caller cannot do.
 *
 * Assignments OUTSIDE that set (on the detail page, when a CD admin opens a
 * user who also holds an IN role) arrive via `lockedAssignments` and render as
 * read-only chips with the reason — visible because hiding them would
 * misrepresent the user's access, locked because a PUT naming IN from this
 * caller is `COUNTRY_SCOPE_VIOLATION` by construction.
 *
 * Native `<select>` elements, deliberately: a handful of roles and at most five
 * countries need keyboard support, a label and nothing else — a Radix menu
 * would be chrome for its own sake here.
 *
 * ===========================================================================
 * THE ROLE LIST COMES FROM THE SERVER NOW — custom-roles design §1.6
 * ===========================================================================
 * ⚠ THIS PICKER WAS `ROLE_NAMES` FROM `domain/rbac.ts` — A HARDCODED SEVEN — AND THAT
 * IS RETIRED WITH THE CALLOUT. Two things were wrong with it once administrators can
 * author roles, and the second was already wrong before:
 *
 *   · it cannot offer a CUSTOM role at all, so the whole authoring feature would be
 *     unassignable through the only screen that assigns;
 *   · it never said what any of them DO. An administrator giving somebody "Tech
 *     Support" could not discover, from any screen in the product, what that person
 *     would then be able to do. That is the recon finding this build closes, and the
 *     cheapest place to close it is here — beside the choice, at the moment it is made.
 *
 * The mirror in `domain/rbac.ts` STAYS: its equality test still guards the seeded names
 * against the shared package, which is what keeps `role-matrix.json`, the seeder and the
 * clients honest with each other. It simply no longer feeds this control.
 *
 * FAIL-SOFT, DELIBERATELY: while the list is loading, or if it fails, Add is disabled
 * with the reason. It does NOT fall back to the hardcoded seven — a fallback would
 * silently offer a stale list on the one screen where being out of date means assigning
 * a role that may not exist, which the server answers with a 422 the operator cannot act
 * on.
 */

import { useId, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, X } from 'lucide-react';
import { Button } from '../../components/ui';
import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import type { CountryCode } from '../../domain/countries';
import type { RoleLabel } from '../../lib/permissions';
import { fetchRoles, roleKeys } from './roles-api';
import type { RoleAssignment } from './users-api';

const SELECT_CLASSES =
  'h-9 rounded-sm border border-line bg-subtle px-2.5 text-body text-fg outline-none';

export interface RoleEditorProps {
  /** The editable assignments — only countries in `countries` ever appear here. */
  value: readonly RoleAssignment[];
  onChange: (next: RoleAssignment[]) => void;
  /** The caller's manageable countries — the ONLY addable ones (see header). */
  countries: readonly CountryCode[];
  /** Out-of-scope assignments, shown locked. Empty for the create form. */
  lockedAssignments?: readonly RoleAssignment[];
  disabled?: boolean;
}

export function RoleAssignmentsEditor({
  value,
  onChange,
  countries,
  lockedAssignments = [],
  disabled = false,
}: RoleEditorProps): React.JSX.Element {
  const countryId = useId();
  const roleId = useId();
  const activeCountry = useCountryCode();
  const { isGroupAdmin } = useSession();
  const [country, setCountry] = useState<CountryCode | ''>(countries[0] ?? '');
  const [role, setRole] = useState<RoleLabel | ''>('');

  const roles = useQuery({
    queryKey: roleKeys.list(),
    queryFn: ({ signal }) => fetchRoles(signal),
    enabled: !disabled,
  });

  const options = roles.data ?? [];
  // The first option is the selection until the operator picks one — the behaviour the
  // hardcoded list had, now derived from what the server actually holds.
  const chosen = options.find((entry) => entry.name === role) ?? options[0];

  const exists = (candidate: RoleAssignment): boolean =>
    value.some(
      (entry) => entry.country === candidate.country && entry.role === candidate.role,
    );

  const add = (): void => {
    if (country === '' || chosen === undefined) return;
    const candidate: RoleAssignment = { country, role: chosen.name };
    // A duplicate row would be sent twice and rendered twice; refuse silently
    // is wrong (the click did nothing), so the Add button is disabled instead —
    // see below.
    if (exists(candidate)) return;
    onChange([...value, candidate]);
  };

  const remove = (candidate: RoleAssignment): void => {
    onChange(
      value.filter(
        (entry) => !(entry.country === candidate.country && entry.role === candidate.role),
      ),
    );
  };

  const duplicate =
    country !== '' && chosen !== undefined && exists({ country, role: chosen.name });

  return (
    <div className="flex flex-col gap-3">
      {value.length === 0 && lockedAssignments.length === 0 ? (
        <p className="text-label text-fg-secondary">
          No roles assigned yet. Add at least one country role below.
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {value.map((entry) => (
            <li
              key={`${entry.country}-${entry.role}`}
              className="flex h-9 items-center justify-between gap-2 rounded-sm border border-line bg-surface px-2.5"
            >
              <span className="flex items-center gap-2 text-body13 text-fg">
                <span className="font-mono text-mono text-fg-secondary">{entry.country}</span>
                {entry.role}
              </span>
              {disabled ? null : (
                <button
                  type="button"
                  onClick={() => remove(entry)}
                  aria-label={`Remove the ${entry.role} role in ${entry.country}`}
                  className="text-fg-muted hover:text-fg"
                >
                  <X aria-hidden="true" className="size-3.5" />
                </button>
              )}
            </li>
          ))}
          {lockedAssignments.map((entry) => (
            <li
              key={`locked-${entry.country}-${entry.role}`}
              className="flex h-9 items-center justify-between gap-2 rounded-sm border border-line bg-subtle px-2.5"
              title={`You do not hold users.manage in ${entry.country}, so this assignment is read-only for you.`}
            >
              <span className="flex items-center gap-2 text-body13 text-fg-secondary">
                <span className="font-mono text-mono">{entry.country}</span>
                {entry.role}
              </span>
              <span className="text-label text-fg-secondary">Outside your scope</span>
            </li>
          ))}
        </ul>
      )}

      {disabled ? null : (
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1.5">
            <label htmlFor={countryId} className="text-label font-medium text-fg">
              Country
            </label>
            <select
              id={countryId}
              value={country}
              onChange={(event) => {
                const next = event.target.value;
                setCountry(next === '' ? '' : (next as CountryCode));
              }}
              className={SELECT_CLASSES}
            >
              {countries.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor={roleId} className="text-label font-medium text-fg">
              Role
            </label>
            <select
              id={roleId}
              value={chosen?.name ?? ''}
              onChange={(event) => setRole(event.target.value)}
              disabled={options.length === 0}
              className={SELECT_CLASSES}
            >
              {options.map((entry) => (
                <option key={entry.id} value={entry.name}>
                  {entry.name}
                </option>
              ))}
            </select>
          </div>
          <Button
            variant="ghost"
            onClick={add}
            disabled={country === '' || duplicate || chosen === undefined}
            title={
              duplicate
                ? 'This role is already assigned in this country.'
                : chosen === undefined
                  ? 'The list of roles has not loaded yet.'
                  : undefined
            }
          >
            <Plus aria-hidden="true" className="size-4" />
            Add role
          </Button>
        </div>
      )}

      {disabled ? null : (
        <div className="flex flex-col gap-1">
          {/*
            WHAT THE CHOICE MEANS, BESIDE THE CHOICE. The permission keys are shown as
            the product's own vocabulary rather than paraphrased: they are what the Roles
            screen shows, what the overrides editor shows and what the server enforces,
            so an administrator learns one set of words. The link goes to the full matrix
            for anybody who wants it grouped by module.
          */}
          {roles.isError ? (
            <p className="text-label text-danger-fg">
              The list of roles could not be loaded, so no role can be added right now.
            </p>
          ) : chosen === undefined ? (
            <p className="text-label text-fg-secondary">Loading the roles…</p>
          ) : (
            <p className="text-label text-fg-secondary">
              <span className="font-medium">{chosen.name}</span>
              {chosen.description === null ? '' : ` — ${chosen.description}`}
              {chosen.permissions.length === 0
                ? ' · no permissions yet'
                : ` · ${chosen.permissions.join(', ')}`}
            </p>
          )}
          <p className="text-label text-fg-secondary">
            <Link
              to={`/countries/${activeCountry}/admin/roles`}
              className="underline underline-offset-2"
            >
              {isGroupAdmin ? 'Create a new role' : 'See what each role can do'}
            </Link>
          </p>
        </div>
      )}
    </div>
  );
}
